import { useEffect, useRef, useState, type CSSProperties } from "react";
import { Play, Sparkles } from "lucide-react";
import { MOTION_LABELS, type MotionStyle } from "@baze/catalog";
import { EFFECTS_FOR, MOTION_SPEEDS, SECTION_ENTERS, type SectionEffect, type SectionEnter, type SectionStyle } from "@baze/schema";
import { Segmented } from "@/components/ui/segmented";
import { Switch } from "@/components/ui/switch";
import { t } from "@/i18n/id";
import { cn } from "@/lib/cn";

type Motion = NonNullable<SectionStyle["motion"]>;
type Tile = SectionEnter | "design";

/** Engine keyframes (packages/engine/src/styles.css) each entrance plays; the app loads that stylesheet. */
const KEYFRAMES: Record<Exclude<SectionEnter, "none" | "typewriter">, string> = {
  fade: "baze-fade",
  rise: "baze-rise",
  zoom: "baze-zoom-in",
  blur: "baze-blur-in",
  "slide-left": "baze-slide-left",
  "slide-right": "baze-slide-right",
  flip: "baze-flip-up",
  curtain: "baze-curtain-up",
  cascade: "baze-rise",
};

const reduced = () => typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/**
 * Plays the section's entrance once on the editor canvas (where motion is otherwise off): the engine's
 * `[data-replay]` rule runs the same keyframes the live site uses.
 */
export function replaySection(sectionId: string) {
  if (reduced()) return;
  const el = document.querySelector<HTMLElement>(`[data-baze-section="${CSS.escape(sectionId)}"]`);
  if (!el) return;
  el.removeAttribute("data-replay");
  void el.offsetWidth; // restart the animation when it is already playing
  el.setAttribute("data-replay", "");
  window.setTimeout(() => el.removeAttribute("data-replay"), 2200);
}

/** "Animasi" block: how the section enters (tiles that show the movement), interactive effects, speed. */
export function MotionPicker({ sectionId, sectionType, motion, designStyle, onChange }: { sectionId: string; sectionType: string; motion: Motion | undefined; designStyle: MotionStyle | undefined; onChange: (m: Motion | undefined) => void }) {
  const m = motion ?? {};
  const effects = EFFECTS_FOR[sectionType] ?? [];
  const active = new Set(m.effects ?? []);
  const set = (patch: Partial<Motion>) => {
    const next: Motion = { ...m, ...patch };
    for (const k of Object.keys(next) as (keyof Motion)[]) if (next[k] === undefined) delete next[k];
    onChange(Object.keys(next).length ? next : undefined);
  };
  // After the definition updates (the canvas re-renders with the new data-enter), play it once.
  const replayNext = useRef(false);
  useEffect(() => {
    if (!replayNext.current) return;
    replayNext.current = false;
    const id = window.setTimeout(() => replaySection(sectionId), 60);
    return () => window.clearTimeout(id);
  }, [m.enter, m.speed, sectionId]);

  const pick = (tile: Tile) => {
    replayNext.current = tile !== "none";
    set({ enter: tile === "design" ? undefined : tile, ...(tile === "none" ? { speed: undefined } : {}) });
  };
  const toggle = (fx: SectionEffect, on: boolean) => {
    const list = effects.filter((e) => (e === fx ? on : active.has(e)));
    set({ effects: list.length ? list : undefined });
  };
  const current: Tile = m.enter ?? "design";

  return (
    <div className="space-y-4">
      <p className="text-[12px] leading-relaxed text-slate-500">{t.editor.style.section.motion.hint}</p>

      <div className="space-y-2">
        <div className="flex items-center justify-between gap-2">
          <p className="text-[12px] font-medium text-slate-500">{t.editor.style.section.motion.enter}</p>
          {current !== "none" ? (
            <button type="button" onClick={() => replaySection(sectionId)} className="inline-flex items-center gap-1 rounded-pill px-2 py-1 text-[12px] font-semibold text-slate-600 hover:bg-slate-100 hover:text-slate-900">
              <Play className="size-3" aria-hidden="true" />
              {t.editor.style.section.motion.replay}
            </button>
          ) : null}
        </div>
        <div role="radiogroup" aria-label={t.editor.style.section.motion.enter} className="grid grid-cols-3 gap-1.5">
          {(["design", ...SECTION_ENTERS] as Tile[]).map((tile) => (
            <EnterTile key={tile} tile={tile} selected={current === tile} onPick={() => pick(tile)} />
          ))}
        </div>
        {current === "design" && designStyle ? <p className="text-[12px] text-slate-500">{t.editor.style.section.motion.followDesignOf(MOTION_LABELS[designStyle])}</p> : null}
      </div>

      {current !== "none" && current !== "design" ? (
        <div className="space-y-2">
          <p className="text-[12px] font-medium text-slate-500">{t.editor.style.section.motion.speed}</p>
          <Segmented
            size="sm"
            full
            label={t.editor.style.section.motion.speed}
            value={m.speed ?? "normal"}
            onChange={(v) => {
              replayNext.current = true;
              set({ speed: v === "normal" ? undefined : v });
            }}
            options={MOTION_SPEEDS.map((s) => ({ value: s, label: t.editor.style.section.motion.speeds[s] }))}
          />
        </div>
      ) : null}

      {effects.length ? (
        <div className="space-y-1">
          <p className="text-[12px] font-medium text-slate-500">{t.editor.style.section.motion.effects}</p>
          <ul className="divide-y divide-line rounded-xl ring-1 ring-line">
            {effects.map((fx) => {
              const copy = t.editor.style.section.motion.effectList[fx];
              const id = `fx-${sectionId}-${fx}`;
              return (
                <li key={fx} className="flex items-center gap-3 px-3 py-2.5">
                  <label htmlFor={id} className="min-w-0 flex-1 cursor-pointer">
                    <span className="block text-[13px] font-medium text-slate-800">{copy.label}</span>
                    <span className="block text-[12px] leading-snug text-slate-500">{copy.hint}</span>
                  </label>
                  <Switch id={id} checked={active.has(fx)} onCheckedChange={(on) => toggle(fx, on)} />
                </li>
              );
            })}
          </ul>
          <p className="pt-1 text-[11px] leading-snug text-slate-400">{t.editor.style.section.motion.effectsHint}</p>
        </div>
      ) : null}
    </div>
  );
}

/** One entrance option: a mini card that performs the movement on hover, focus, and when picked. */
function EnterTile({ tile, selected, onPick }: { tile: Tile; selected: boolean; onPick: () => void }) {
  const [run, setRun] = useState(0);
  const typeRef = useRef<HTMLSpanElement>(null);
  const play = () => {
    if (reduced() || tile === "none" || tile === "design") return;
    setRun((n) => n + 1);
    if (tile === "typewriter") typeRef.current?.animate([{ clipPath: "inset(0 100% 0 0)" }, { clipPath: "inset(0 0 0 0)" }], { duration: 900, easing: "steps(7, end)", fill: "both" });
  };
  const label = tile === "design" ? t.editor.style.section.motion.followDesign : t.editor.style.section.motion.enters[tile];
  const anim = (i: number): CSSProperties | undefined =>
    run && tile !== "design" && tile !== "none" && tile !== "typewriter"
      ? { animation: `${KEYFRAMES[tile]} 0.75s cubic-bezier(0.2, 0.7, 0.2, 1) ${i * (tile === "cascade" ? 160 : 70)}ms both` }
      : undefined;

  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      onClick={() => {
        onPick();
        play();
      }}
      onPointerEnter={play}
      onFocus={play}
      className="group min-w-0 rounded-xl p-1 text-center outline-none"
    >
      <span
        className="relative grid aspect-[4/3] place-items-center overflow-hidden rounded-lg bg-slate-50 ring-1 ring-line transition group-hover:ring-slate-300 group-focus-visible:ring-2 group-focus-visible:ring-primary group-aria-checked:bg-white group-aria-checked:ring-2 group-aria-checked:ring-primary"
        style={{ perspective: "400px" }}
        aria-hidden="true"
      >
        {tile === "none" ? (
          <span className="flex w-3/5 flex-col gap-1">
            <span className="h-1.5 w-full rounded-full bg-slate-300" />
            <span className="h-1.5 w-2/3 rounded-full bg-slate-300" />
          </span>
        ) : tile === "typewriter" ? (
          <span ref={typeRef} className="font-serif text-[15px] font-semibold tracking-tight text-slate-700">
            Halo|
          </span>
        ) : tile === "design" ? (
          <Sparkles className="size-4 text-slate-400" />
        ) : (
          <span key={run} className="flex w-3/5 flex-col gap-1">
            <span className="h-2 w-full rounded-sm bg-slate-700/80" style={anim(0)} />
            <span className="h-1.5 w-4/5 rounded-full bg-slate-300" style={anim(1)} />
            <span className="h-1.5 w-3/5 rounded-full bg-slate-300" style={anim(2)} />
          </span>
        )}
      </span>
      <span className={cn("mt-1 block truncate text-[11px] font-medium", selected ? "text-slate-900" : "text-slate-500")}>{label}</span>
    </button>
  );
}

/** Keyframe that gives each site-wide motion style its character, for the "Gaya gerak" tiles. */
export const STYLE_KEYFRAMES: Record<MotionStyle, string> = {
  subtle: "baze-rise",
  cinematic: "baze-blur-rise",
  playful: "baze-spring",
  editorial: "baze-line-in",
  dreamy: "baze-dream",
  bold: "baze-snap",
  kinetic: "baze-skew-rise",
  parallax: "baze-depth-rise",
  paper: "baze-unfold",
  retro: "baze-stamp",
  celestial: "baze-glow-in",
  pixel: "baze-pixel-pop",
};

/** A mini card (title bar + two lines) that performs `keyframes` when the pointer or focus reaches it. */
export function MotionDemo({ keyframes, color }: { keyframes: string; color: string }) {
  const [run, setRun] = useState(0);
  const play = () => !reduced() && setRun((n) => n + 1);
  const anim = (i: number): CSSProperties | undefined => (run ? { animation: `${keyframes} 0.8s cubic-bezier(0.2, 0.7, 0.2, 1) ${i * 80}ms both` } : undefined);
  return (
    <span className="grid h-full w-full place-items-center bg-slate-50" onPointerEnter={play} style={{ perspective: "400px" }}>
      <span key={run} className="flex w-3/5 flex-col gap-1">
        <span className="h-2 w-full rounded-sm" style={{ background: color, ...anim(0) }} />
        <span className="h-1.5 w-4/5 rounded-full bg-slate-300" style={anim(1)} />
        <span className="h-1.5 w-3/5 rounded-full bg-slate-300" style={anim(2)} />
      </span>
    </span>
  );
}
